import { useState, useCallback } from 'react';
import apiUser from '../../api/apiUser';
import { useNotification } from '../../context/NotificationContext';

export const useCheckout = () => {
    const [loading, setLoading] = useState(false);
    const { showNotification } = useNotification();

    const checkEmail = useCallback(async (email) => {
        setLoading(true);
        try {
            const response = await apiUser.post('/checkout/check-email', { email });
            return response.data;
        } catch (error) {
            console.error('Error checking email:', error);
            return { exists: false };
        } finally {
            setLoading(false);
        }
    }, []);

    const sendOtp = useCallback(async (email) => {
        setLoading(true);
        try {
            const response = await apiUser.post('/checkout/otp/send', { email });
            if (response.data.status === 'success') {
                showNotification({
                    type: 'success',
                    title: 'OTP Sent',
                    message: 'Please check your email for the verification code.'
                });
                return true;
            } else {
                showNotification({
                    type: 'failed',
                    title: 'Email Error',
                    message: response.data.message || 'Failed to send verification code.'
                });
                return false;
            }
        } catch (error) {
            showNotification({
                type: 'failed',
                title: 'Error',
                message: 'An unexpected error occurred. Please try again.'
            });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const verifyOtp = useCallback(async (email, otp) => {
        setLoading(true);
        try {
            const response = await apiUser.post('/checkout/otp/verify', { email, otp });
            if (response.data.status === 'success') {
                showNotification({
                    type: 'success',
                    title: 'Verified',
                    message: 'Email verified successfully.'
                });
                return true;
            } else {
                showNotification({
                    type: 'failed',
                    title: 'Verification Failed',
                    message: response.data.message || 'Invalid verification code.'
                });
                return false;
            }
        } catch (error) {
             showNotification({
                type: 'failed',
                title: 'Error',
                message: 'OTP verification failed. Please try again.'
            });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const submitCheckout = useCallback(async (checkoutData) => {
        setLoading(true);
        try {
            const response = await apiUser.post('/checkout/submit', checkoutData);
            if (response.data.status === 'success') {
                return { success: true, data: response.data };
            } else {
                showNotification({
                    type: 'failed',
                    title: 'Checkout Failed',
                    message: response.data.message || 'Failed to complete order.'
                });
                return { success: false };
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to submit checkout. Please check your network and try again.';
            showNotification({
                type: 'failed',
                title: 'Submission Error',
                message: errorMsg
            });
            return { success: false };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const getInvoiceSettings = useCallback(async () => {
        // We can reuse the existing endpoint or create a specifically public one
        // For now let's assume we have a public settings endpoint for checkout fees
        try {
            const response = await apiUser.get('/user/settings'); // Corrected endpoint
            return response.data;
        } catch (error) {
            console.error('Error fetching settings:', error);
            return null;
        }
    }, []);

    return {
        loading,
        checkEmail,
        sendOtp,
        verifyOtp,
        submitCheckout,
        getInvoiceSettings
    };
};
